import {useState} from "react"
import {apiCart, apiShipping} from "./Api"
import {cartLangMap, cartShippingCountryDefault} from "../settings"

export const useCart = () => {
    const [stateCart, setStateCart] = useState({})
    const [stateLoad, setStateLoad] = useState(true)
    const [stateError, setStateError] = useState(null)
    const [shippingCountries, setShippingCountries] = useState([])
    const [shippingCountry, setShippingCountry] = useState(cartShippingCountryDefault)

    const load = async (solutionId, lang) => {
        if (solutionId && lang) {
            try {
                setStateLoad(true)
                if (!shippingCountries.length) {
                    const shippingResponse = await apiShipping()
                    setShippingCountries(shippingResponse?.shippingCountries || [])
                }

                const cardResponse = await apiCart(stateCart.cartId, solutionId, cartLangMap[lang], shippingCountry)
                setStateCart(apiDataToState(cardResponse))
                setError(null)
            } catch (error) {
                setError(error)
            }
        }
    }

    const setError = (message) => {
        setStateLoad(false)
        setStateError(message)
    }

    const shippingGetOptions = () => {
        return shippingCountries.map(country => ({
            code: country,
            name: country + "-name"
        })).sort((a, b) => {
            if (a.name < b.name) return -1
            if (a.name > b.name) return 1
            return 0;
        })
    }

    const shippingGet = () => {
        return shippingCountry
    }

    const shippingSet = (country) => {
        setShippingCountry(country)
    }

    const isLoading = () => {
        return stateLoad === true
    }

    const getItems = () => {
        return stateCart?.items ?? []
    }

    const getTotal = () => {
        return stateCart?.totals?.grandTotal ?? 0
    }

    const hasLink = () => {
        return !!stateCart?.cartLink
    }

    const isQuote = () => {
        return !!stateCart?.isQuote
    }

    const hasError = () => {
        return stateError !== null
    }

    const getError = () => {
        return typeof stateError == "string" ? stateError : stateError?.title
    }

    const hasData = () => {
        return !hasError() && getTotal()
    }

    const gotoCart = () => {
        if (stateCart?.cartLink) {
            const form = document.createElement("form")
            form.method = "POST"
            form.target = "_blank"
            form.action = stateCart.cartLink
            document.body.appendChild(form)
            form.submit()
            form.remove()
        }
    }

    return {
        load,
        isLoading,
        getItems,
        getTotal,
        gotoCart,
        hasLink,
        isQuote,
        hasError,
        getError,
        hasData,
        shippingGetOptions,
        shippingGet,
        shippingSet,
        setError,
    }
}

const apiDataToState = apiData => {

    const items = apiData.items.map(item => {
        return {
            sku: item.sku,
            name: item.name,
            qty: item.qty,
            price: item.rowTotalInclTax,
            options: item.options,
            image: null,
        }
    })

    const tax = getTotalValue(apiData, "tax")
    const grandTotalInclTax = getTotalValue(apiData, "grand_total")
    const grandTotal = grandTotalInclTax - tax
    const taxRatePercent = Math.round((grandTotalInclTax / grandTotal - 1) * 100)

    return {
        cartId: apiData.cartId,
        items: items,
        totals: {
            subTotal: getTotalValue(apiData, "subtotal"),
            shipping: getTotalValue(apiData, "shipping"),
            grandTotal: grandTotalInclTax,
            tax: tax,
            taxRate: taxRatePercent,
        },
        cartLink: apiData.cartLink,
        currencyCode: apiData.quoteCurrencyCode,
        isQuote: apiData.quoteOnly,
    }
}

const getTotalValue = (apiData, code) => {
    return apiData.totalSegments?.find(total => total.code === code)?.value ?? 0.0
}
